import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from './useSession';
import { fetchMe, type Me } from '../lib/me';
import { getAuthUser } from '../lib/ws';
import { applyUserLanguage } from '../i18n';

/** Current user's profile from the API (real Telegram only — needs a session
 *  token). Outside Telegram (dev/browser) `token`/`me` are null and screens fall
 *  back to the local profile store. Mutations live in lib/me.ts — call them with
 *  `token`, then `refresh()`. */
export function useMe() {
  const { token } = useSession();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['me'],
    enabled: !!token,
    queryFn: () => fetchMe(token!),
    // Seed from the auth handshake's public user so `me` is available on the very
    // first render (no waiting on a /me round-trip before the open reauth gate can
    // decide). A background refetch still refreshes it.
    initialData: () => (getAuthUser() as Me | null) ?? undefined,
    refetchOnMount: 'always',
    // No polling heartbeat: a session terminated on another device is pushed
    // instantly over the WebSocket (`session_revoked` → logout) — see useRealtime.
  });

  // Re-apply the server-saved language whenever /me resolves.
  useEffect(() => {
    if (q.data?.language) applyUserLanguage(q.data.language);
  }, [q.data?.language]);

  return {
    me: q.data ?? null,
    token,
    loading: q.isLoading,
    refresh: () => qc.invalidateQueries({ queryKey: ['me'] }),
  };
}
